import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

const RECONCILE_COOLDOWN_MS = 30_000
const lastReconcileAt = new Map()

function currentUserKey() {
  return document.querySelector("[data-notification-badge-user-id-value]")
    ?.dataset.notificationBadgeUserIdValue || "anonymous"
}

// Keeps the Home feed current without replacing the page. Matching runs in a
// background job in production; MatchingChannel announces completion, then this
// controller asks Rails for only the confirmed cards created since the last check.
export default class extends Controller {
  static values = { url: String, after: String }

  connect() {
    this._showManualResult()
    this._onManualMatchingFinished = async event => {
      try {
        await this.refresh()
        this._showManualResult()
        await Promise.race([
          Promise.all(Array.from(this.element.querySelectorAll("img"))
            .filter(image => image.loading !== "lazy")
            .map(image => image.decode?.().catch(() => {}))),
          new Promise(resolve => setTimeout(resolve, 1000))
        ])
        await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      } finally { event.detail?.complete?.() }
    }
    document.addEventListener("manual:matching-finished", this._onManualMatchingFinished)
    this._connectFeed()
  }

  _showManualResult() {
    const resultKey = `manual-matching-result:${currentUserKey()}`
    try {
      const result = JSON.parse(sessionStorage.getItem(resultKey) || "null")
      sessionStorage.removeItem(resultKey)
      if (result?.matches === false && Date.now() - result.at < 10_000) {
        const notice = document.createElement("p")
        notice.className = "manual-matching-result"
        notice.setAttribute("role", "status")
        notice.textContent = "No new matches found."
        this.element.querySelector(".manual-matching-result")?.remove()
        this.element.prepend(notice)
      }
    } catch (_) { /* Storage can be unavailable in a restricted webview. */ }
  }

  _connectFeed() {
    this._onRestore = () => this.refresh({ reconcile: true })
    this._onVisibilityChange = () => {
      if (document.visibilityState === "visible") this.refresh({ reconcile: true })
    }

    document.addEventListener("native:restore", this._onRestore)
    document.addEventListener("visibilitychange", this._onVisibilityChange)

    // Subscribe directly instead of relying on the native photo bridge to relay
    // cable events. This keeps the other device's timeline live on both web and iOS.
    this._subscription = consumer.subscriptions.create(
      { channel: "MatchingChannel" },
      {
        connected: () => this.refresh({ reconcile: true }),
        received: (data) => {
          if (["matching_complete", "timeline_updated"].includes(data?.type)) this.refresh()
        }
      }
    )

    // Close the small race between the server rendering Home and the cable
    // subscription becoming active. This is also the reconciliation path when
    // Turbo restores Home from its cache.
    this.refresh({ reconcile: true })
  }

  disconnect() {
    document.removeEventListener("manual:matching-finished", this._onManualMatchingFinished)
    document.removeEventListener("native:restore", this._onRestore)
    document.removeEventListener("visibilitychange", this._onVisibilityChange)
    this._subscription?.unsubscribe()
  }

  async refresh({ reconcile = false } = {}) {
    if (!this.hasUrlValue || !this.hasAfterValue) return
    if (reconcile) {
      const now = Date.now()
      const userKey = currentUserKey()
      if (now - (lastReconcileAt.get(userKey) || 0) < RECONCILE_COOLDOWN_MS) return
      lastReconcileAt.set(userKey, now)
    }
    if (this._refreshing) {
      // Cable reconnects can call connected() repeatedly while the initial
      // reconciliation is still running. One reconciliation is enough; only a
      // real matching broadcast needs a follow-up pass.
      if (reconcile) return
      this._refreshAgain = true
      return
    }

    this._refreshing = true
    try {
      let until = null
      let batchAfter = null
      let batchAfterId = null

      do {
        const url = new URL(this.urlValue, window.location.origin)
        url.searchParams.set("after", this.afterValue)
        if (until) url.searchParams.set("until", until)
        if (batchAfter) {
          url.searchParams.set("batch_after", batchAfter)
          url.searchParams.set("batch_after_id", batchAfterId)
        }

        const response = await fetch(url, {
          headers: { "Accept": "text/vnd.turbo-stream.html" },
          credentials: "same-origin"
        })
        if (!response.ok) throw new Error(`Feed refresh failed (${response.status})`)

        const stream = await response.text()
        if (stream) this._renderPreservingPosition(stream)

        until = response.headers.get("X-Feed-Updates-Until") || until
        const hasMore = response.headers.get("X-Feed-Updates-Has-More") === "true"
        if (hasMore) {
          batchAfter = response.headers.get("X-Feed-Updates-Next-After")
          batchAfterId = response.headers.get("X-Feed-Updates-Next-Id")
          if (!until || !batchAfter || !batchAfterId) throw new Error("Feed refresh pagination headers missing")
        } else {
          const nextCursor = response.headers.get("X-Feed-Updates-After")
          if (nextCursor) this.afterValue = nextCursor
          batchAfter = null
        }
      } while (batchAfter)
    } catch (error) {
      console.warn("[feed-updates] refresh failed", error)
    } finally {
      this._refreshing = false
      if (this._refreshAgain) {
        this._refreshAgain = false
        this.refresh()
      }
    }
  }

  _renderPreservingPosition(stream) {
    const feed = document.getElementById("feed-list")
    const heightBefore = feed?.scrollHeight || 0
    const scrollBefore = window.scrollY

    window.Turbo.renderStreamMessage(stream)

    // At the top, reveal new cards naturally. Further down, compensate for the
    // prepended height so the content under the user's finger does not jump.
    if (!feed || scrollBefore < 80) return
    requestAnimationFrame(() => {
      const addedHeight = feed.scrollHeight - heightBefore
      if (addedHeight > 0) window.scrollBy(0, addedHeight)
    })
  }
}
